import React, { useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FiCheckSquare } from 'react-icons/fi';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import { useChildProfile } from './ChildProfileLayout';
import { useMilestones } from '../../hooks/useMilestones';
import {
    PageHeader,
    EmptyState,
    SkeletonTableRow,
    SkeletonTabs,
    useToast,
} from '../../theme/components';
import { parseApiError } from '../../api/errors';
import { PageButton } from '../../theme/components/PageButton';
import { SELECTIONS, SELECTION_LABELS, ROW_SEVERITY } from '../../data/milestones';
import { STATUS, perRowStatus } from '../../utils/milestonesFilter';
import { monthsBetween } from '../../utils/dates';
import './Milestones.css';

/**
 * Two-state YES/NO toggle row. Clicking an active option clears it
 * (back to null); clicking the inactive option switches to it.
 * Implemented as an ARIA radiogroup (mutually-exclusive choices with a
 * clear affordance) — not native checkboxes — since checkboxes carry
 * "select multiple" semantics that don't fit a single yes/no pick.
 */
function MilestoneToggleRow({ value, onChange, name }) {
    return (
        <div className="ms-toggle" role="radiogroup" aria-label={name}>
            {SELECTIONS.map((s) => {
                const active = value === s;
                const severity = ROW_SEVERITY[s];
                return (
                    <button
                        key={s}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`ms-toggle__btn ms-toggle__btn--${severity}${active ? ' is-active' : ''}`}
                        onClick={() => onChange(active ? null : s)}
                        title={active ? 'Click to clear' : `Mark as ${SELECTION_LABELS[s]}`}
                    >
                        {SELECTION_LABELS[s]}
                    </button>
                );
            })}
        </div>
    );
}

const Milestones = () => {
    const { id } = useParams();
    const toast = useToast();
    const theme = useTheme();

    const { loading: profileLoading, data: profile } = useChildProfile();
    const { items, loading, error, refetch, bulkUpsert } = useMilestones(id);

    // Whole completed months between the child's DOB and today — drives
    // perRowStatus() so the PHM can see at a glance which bands are
    // at risk (DELAYED = past 1-month grace without a 'yes').
    const ageMonths = useMemo(
        () => monthsBetween(profile?.dob),
        [profile?.dob],
    );

    // MUI chip styling for per-row status badges. Unrecorded rows show
    // PENDING / DUE / DELAYED / NOT YET; recorded rows rely on the
    // existing row tint (ms-row--success / ms-row--warning) and skip
    // the extra chip to keep the table clean.
    const statusChipSx = (status) => {
        switch (status) {
            case STATUS.PENDING:
                return {
                    bgcolor: theme.palette.warning.light,
                    color: theme.palette.warning.contrastText ?? '#7c2d12',
                    fontWeight: 600,
                };
            case STATUS.DUE:
                return {
                    bgcolor: theme.palette.warning.main,
                    color: '#fff',
                    fontWeight: 600,
                };
            case STATUS.DELAYED:
                return {
                    bgcolor: theme.palette.error.main,
                    color: '#fff',
                    fontWeight: 700,
                };
            case STATUS.ACHIEVED:
                return {
                    bgcolor: theme.palette.success.main,
                    color: '#fff',
                    fontWeight: 600,
                };
            default:
                return null;
        }
    };

    // Optimistic local overrides keyed by milestone key.
    const [localSelections, setLocalSelections] = useState({});

    // Group rows by age-band order (year int 1..9) for the rowspan pattern.
    const rowsByBand = useMemo(() => {
        const grouped = new Map();
        for (const m of items) {
            const key = m.ageBand || String(m.year);
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key).push(m);
        }
        return [...grouped.entries()].sort((a, b) => {
            // Items in the same band share the same `year`; sort bands by it.
            const aYear = a[1][0]?.year ?? 0;
            const bYear = b[1][0]?.year ?? 0;
            return aYear - bYear;
        });
    }, [items]);

    const onChangeSelection = useCallback(async (key, selection) => {
        const before = localSelections[key] ?? null;
        setLocalSelections((prev) => ({ ...prev, [key]: selection }));
        try {
            await bulkUpsert([{ milestoneKey: key, selection }]);
        } catch (err) {
            setLocalSelections((prev) => ({ ...prev, [key]: before }));
            toast.error(parseApiError(err, 'Failed to save selection.'));
        }
    }, [bulkUpsert, toast, localSelections]);

    if (profileLoading) {
        return (
            <div>
                <SkeletonTabs />
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <EmptyState
                    illustration="error"
                    title="Couldn't load milestones"
                    description={parseApiError(error, 'Unknown error')}
                    action={
                        <PageButton type="button" variant="primary" onClick={refetch}>
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    return (
        <div>
            <PageHeader
                title="Developmental Milestones"
                subtitle="Mark each milestone as YES or NO. Saved automatically as you go."
                icon={FiCheckSquare}
                className="cp-section-header"
            />

            <div className="ms-card">
                <div className="ms-table-wrap">
                    <table className="ms-table">
                        <thead>
                            <tr>
                                <th className="ms-th-year">Age Band</th>
                                <th>Activity</th>
                                <th className="ms-th-timing">YES / NO</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading
                                ? Array.from({ length: 8 }).map((_, i) => (
                                    <SkeletonTableRow key={i} cols={3} />
                                ))
                                : rowsByBand.map(([band, ms]) => (
                                    <React.Fragment key={band}>
                                        {ms.map((m, idx) => {
                                            const value = localSelections[m.key] ?? m.selection ?? null;
                                            // Unrecorded rows get an at-a-glance
                                            // status badge. Marked YES / NO
                                            // rows keep their row tint and
                                            // skip the extra chip — the toggle
                                            // already tells the story.
                                            const rowStatus = value
                                                ? null
                                                : perRowStatus(null, ageMonths, m.ageBandUpperMonths);
                                            return (
                                                <tr
                                                    key={m.key}
                                                    className={
                                                        value ? `ms-row ms-row--${ROW_SEVERITY[value]}` : 'ms-row'
                                                    }
                                                >
                                                    {idx === 0 && (
                                                        <td
                                                            rowSpan={ms.length}
                                                            className="ms-year"
                                                        >
                                                            <div className="ms-year-stamp">
                                                                <div className="ms-year-stamp__big">
                                                                    {band}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    )}
                                                    <td>
                                                        <div className="ms-activity">
                                                            <span className="ms-activity__label">{m.label}</span>
                                                            <span className="ms-activity__desc">{m.description}</span>
                                                            {rowStatus && statusChipSx(rowStatus) ? (
                                                                <Chip
                                                                    size="small"
                                                                    label={rowStatus}
                                                                    sx={{
                                                                        mt: 0.5,
                                                                        alignSelf: 'flex-start',
                                                                        ...statusChipSx(rowStatus),
                                                                    }}
                                                                />
                                                            ) : null}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <MilestoneToggleRow
                                                            name={m.label}
                                                            value={value}
                                                            onChange={(v) => onChangeSelection(m.key, v)}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Milestones;
